import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  LinearProgress,
  Stack,
  TextField,
  Typography
} from '@mui/material';

const API_URL = process.env.REACT_APP_API_URL || '';

export default function StaffingSelfRegisterPage() {
  const { token } = useParams();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [companyName, setCompanyName] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(null);

  const [form, setForm] = useState({
    full_name: '',
    email: '',
    mobile: '',
    address: '',
    qualification: '',
    course: '',
    specialization: '',
    experience_years: '',
    emergency_contact: '',
    emergency_name: '',
    password: '',
    password_confirm: ''
  });
  const [resume, setResume] = useState(null);
  const [educationProof, setEducationProof] = useState(null);
  const [otherDocs, setOtherDocs] = useState([]);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      setLoading(true);
      setError('');
      if (!API_URL) {
        setError('App is not configured (missing REACT_APP_API_URL).');
        setLoading(false);
        return;
      }
      try {
        const res = await fetch(`${API_URL}/api/public/staffing-register/${token}`);
        const data = await res.json().catch(() => ({}));
        if (cancelled) return;
        if (!res.ok || !data.success) {
          setError(data.error || 'Invalid or inactive registration link.');
          setLoading(false);
          return;
        }
        setCompanyName(data.company_name || 'Employer');
      } catch {
        if (!cancelled) setError('Could not load this page. Check your connection.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    if (token) load();
    else setError('Missing registration token.');
    return () => {
      cancelled = true;
    };
  }, [token]);

  const onSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!resume) {
      setError('Please upload your resume.');
      return;
    }
    if (form.password || form.password_confirm) {
      if (form.password.length < 6) {
        setError('Password must be at least 6 characters, or leave both password fields empty.');
        return;
      }
      if (form.password !== form.password_confirm) {
        setError('Passwords do not match.');
        return;
      }
    }
    if (!API_URL) {
      setError('App is not configured (missing REACT_APP_API_URL).');
      return;
    }
    setSubmitting(true);
    try {
      const fd = new FormData();
      Object.entries(form).forEach(([k, v]) => {
        if (k === 'password_confirm') return;
        fd.append(k, v ?? '');
      });
      fd.append('resume', resume);
      if (educationProof) fd.append('education_proof', educationProof);
      otherDocs.forEach((file) => fd.append('other_documents', file));

      const res = await fetch(`${API_URL}/api/public/staffing-register/${token}/submit`, {
        method: 'POST',
        body: fd
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(data.error || 'Registration failed');
      }
      setDone({
        message: data.message || 'Registration submitted.',
        temporary_password: data.temporary_password,
        account_created: data.account_created
      });
    } catch (err) {
      setError(err.message || 'Registration failed');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ maxWidth: 560, mx: 'auto', mt: 6, px: 2 }}>
        <LinearProgress />
        <Typography align="center" sx={{ mt: 2 }} color="text.secondary">
          Loading…
        </Typography>
      </Box>
    );
  }

  if (error && !companyName) {
    return (
      <Box sx={{ maxWidth: 560, mx: 'auto', mt: 6, px: 2 }}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  if (done) {
    return (
      <Box sx={{ maxWidth: 560, mx: 'auto', mt: 6, px: 2 }}>
        <Card elevation={2} sx={{ borderRadius: 3 }}>
          <CardContent sx={{ py: 4 }}>
            <Typography variant="h5" fontWeight={800} gutterBottom align="center">
              Thank you
            </Typography>
            <Typography color="text.secondary" align="center" paragraph>
              {done.message}
            </Typography>
            {done.temporary_password && (
              <Alert severity="info" sx={{ mt: 2 }}>
                A login password was set for the candidate app: <strong>{done.temporary_password}</strong>
                <Typography variant="caption" display="block" sx={{ mt: 1 }}>
                  Save this password. You can change it after logging in as a candidate.
                </Typography>
              </Alert>
            )}
            {done.account_created && !done.temporary_password && (
              <Alert severity="info" sx={{ mt: 2 }}>
                Your account password was saved. You can log in to the candidate app with this mobile number.
              </Alert>
            )}
          </CardContent>
        </Card>
      </Box>
    );
  }

  return (
    <Box sx={{ maxWidth: 640, mx: 'auto', mt: 4, mb: 6, px: 2 }}>
      <Card elevation={2} sx={{ borderRadius: 3 }}>
        <CardContent sx={{ py: 3 }}>
          <Typography variant="h5" fontWeight={800} gutterBottom>
            Register with {companyName}
          </Typography>
          <Typography color="text.secondary" sx={{ mb: 2 }}>
            Enter your details and upload documents. You do not need the company to add you first — submit this form once to
            join their selection list.
          </Typography>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}
          <Box component="form" onSubmit={onSubmit}>
            <Stack spacing={2}>
              <TextField
                required
                label="Full name"
                value={form.full_name}
                onChange={(e) => setForm((p) => ({ ...p, full_name: e.target.value }))}
                fullWidth
                size="small"
              />
              <TextField
                label="Email"
                type="email"
                value={form.email}
                onChange={(e) => setForm((p) => ({ ...p, email: e.target.value }))}
                fullWidth
                size="small"
              />
              <TextField
                required
                label="Mobile (10 digits)"
                value={form.mobile}
                onChange={(e) => setForm((p) => ({ ...p, mobile: e.target.value.replace(/\D/g, '').slice(0, 10) }))}
                fullWidth
                size="small"
                inputProps={{ maxLength: 10 }}
              />
              <TextField
                label="Address"
                value={form.address}
                onChange={(e) => setForm((p) => ({ ...p, address: e.target.value }))}
                fullWidth
                size="small"
                multiline
                minRows={2}
              />
              <TextField
                label="Qualification"
                value={form.qualification}
                onChange={(e) => setForm((p) => ({ ...p, qualification: e.target.value }))}
                fullWidth
                size="small"
              />
              <TextField
                label="Course"
                value={form.course}
                onChange={(e) => setForm((p) => ({ ...p, course: e.target.value }))}
                fullWidth
                size="small"
              />
              <TextField
                label="Specialization"
                value={form.specialization}
                onChange={(e) => setForm((p) => ({ ...p, specialization: e.target.value }))}
                fullWidth
                size="small"
              />
              <TextField
                label="Experience (years)"
                value={form.experience_years}
                onChange={(e) => setForm((p) => ({ ...p, experience_years: e.target.value }))}
                fullWidth
                size="small"
              />
              <TextField
                label="Emergency contact name"
                value={form.emergency_name}
                onChange={(e) => setForm((p) => ({ ...p, emergency_name: e.target.value }))}
                fullWidth
                size="small"
              />
              <TextField
                label="Emergency contact number"
                value={form.emergency_contact}
                onChange={(e) => setForm((p) => ({ ...p, emergency_contact: e.target.value }))}
                fullWidth
                size="small"
              />
              <Typography variant="subtitle2" color="text.secondary">
                Optional: set a password for the candidate app (otherwise a temporary password may be emailed or shown after
                submit).
              </Typography>
              <TextField
                label="Password (optional)"
                type="password"
                value={form.password}
                onChange={(e) => setForm((p) => ({ ...p, password: e.target.value }))}
                fullWidth
                size="small"
                autoComplete="new-password"
              />
              <TextField
                label="Confirm password"
                type="password"
                value={form.password_confirm}
                onChange={(e) => setForm((p) => ({ ...p, password_confirm: e.target.value }))}
                fullWidth
                size="small"
                autoComplete="new-password"
              />

              <Box>
                <Typography variant="subtitle2" fontWeight={700} gutterBottom>
                  Resume (required)
                </Typography>
                <Button variant="outlined" component="label" size="small">
                  Choose file
                  <input
                    type="file"
                    hidden
                    accept=".pdf,.doc,.docx"
                    onChange={(e) => setResume(e.target.files?.[0] || null)}
                  />
                </Button>
                <Typography variant="caption" display="block" color="text.secondary" sx={{ mt: 0.5 }}>
                  {resume?.name || 'No file selected'}
                </Typography>
              </Box>

              <Box>
                <Typography variant="subtitle2" fontWeight={700} gutterBottom>
                  Education proof (optional)
                </Typography>
                <Button variant="outlined" component="label" size="small">
                  Choose file
                  <input
                    type="file"
                    hidden
                    accept=".pdf,.jpg,.jpeg,.png"
                    onChange={(e) => setEducationProof(e.target.files?.[0] || null)}
                  />
                </Button>
                <Typography variant="caption" display="block" color="text.secondary" sx={{ mt: 0.5 }}>
                  {educationProof?.name || 'No file selected'}
                </Typography>
              </Box>

              <Box>
                <Typography variant="subtitle2" fontWeight={700} gutterBottom>
                  Other documents (optional, up to 5)
                </Typography>
                <Button variant="outlined" component="label" size="small">
                  Choose files
                  <input
                    type="file"
                    hidden
                    multiple
                    accept=".pdf,.jpg,.jpeg,.png"
                    onChange={(e) => setOtherDocs(Array.from(e.target.files || []).slice(0, 5))}
                  />
                </Button>
                <Typography variant="caption" display="block" color="text.secondary" sx={{ mt: 0.5 }}>
                  {otherDocs.length ? `${otherDocs.length} file(s)` : 'No files selected'}
                </Typography>
              </Box>

              <Button type="submit" variant="contained" size="large" disabled={submitting} sx={{ mt: 1 }}>
                {submitting ? 'Submitting…' : 'Submit registration'}
              </Button>
            </Stack>
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
}
